import axiosInstance from './axios.config';
import {
  LoginRequest,
  LoginResponse,
  SelectProfileRequest,
  RefreshTokenResponse,
  User,
} from '../types/user.types';

class AuthService {
  /**
   * Connexion de l'utilisateur
   * @param usernameOrEmail - Nom d'utilisateur ou email
   * @param password - Mot de passe
   * @param rememberMe - Se souvenir de moi
   */
  async login(usernameOrEmail: string, password: string, rememberMe: boolean = false): Promise<LoginResponse> {
    const response = await axiosInstance.post<LoginResponse>('/auth/login', {
      username: usernameOrEmail, // Le backend attend "username"
      password,
      rememberMe,
    });

    const { access_token, refresh_token, user, availableProfiles } = response.data;

    // Sauvegarder les tokens
    this.setToken(access_token);
    this.setRefreshToken(refresh_token);

    // Sauvegarder l'utilisateur
    localStorage.setItem('user', JSON.stringify(user));

    // Sauvegarder les profils disponibles
    if (availableProfiles && availableProfiles.length > 0) {
      localStorage.setItem('available_profiles', JSON.stringify(availableProfiles));
    }

    // Sauvegarder l'identifiant si "Se souvenir de moi"
    if (rememberMe) {
      localStorage.setItem('remembered_email', usernameOrEmail);
    }

    return response.data;
  }

  /**
   * Sélection d'un profil (multi-profil)
   */
  async selectProfile(profileId: string): Promise<LoginResponse> {
    const response = await axiosInstance.post<LoginResponse>('/auth/select-profile', {
      profileId,
    });

    const { access_token, refresh_token, user } = response.data;

    // Mettre à jour les tokens
    this.setToken(access_token);
    this.setRefreshToken(refresh_token);

    // Mettre à jour l'utilisateur
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('selected_profile', profileId);

    return response.data;
  }

  /**
   * Déconnexion de l'utilisateur
   */
  async logout(): Promise<void> {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le localStorage
      this.clearStorage();

      // Rediriger vers la page de connexion
      window.location.href = '/sigp/login';
    }
  }

  /**
   * Rafraîchir le token
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axiosInstance.post<RefreshTokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token: new_refresh_token } = response.data;

    this.setToken(access_token);
    this.setRefreshToken(new_refresh_token);

    return response.data;
  }

  /**
   * Obtenir l'utilisateur courant
   */
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Erreur lors de la lecture de l\'utilisateur:', error);
      return null;
    }
  }

  /**
   * Vérifier si l'utilisateur est connecté
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();

    return !!(token && user);
  }

  /**
   * Sauvegarder le token d'accès
   */
  setToken(token: string): void {
    localStorage.setItem('access_token', token);
  }

  /**
   * Obtenir le token d'accès
   */
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Sauvegarder le token de rafraîchissement
   */
  setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  /**
   * Obtenir le token de rafraîchissement
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Nettoyer le localStorage
   */
  clearStorage(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('selected_profile');
    localStorage.removeItem('available_profiles');
  }

  /**
   * Obtenir l'email mémorisé
   */
  getRememberedEmail(): string | null {
    return localStorage.getItem('remembered_email');
  }

  /**
   * Changer le mot de passe
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await axiosInstance.post('/auth/change-password', {
      oldPassword,
      newPassword,
    });
  }

  /**
   * Demander un reset de mot de passe (envoie un email avec un lien)
   */
  async requestPasswordReset(email: string): Promise<void> {
    await axiosInstance.post('/auth/forgot-password', {
      email,
    });
  }

  /**
   * Réinitialiser le mot de passe
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await axiosInstance.post('/auth/reset-password', {
      token,
      newPassword,
    });
  }
}

export const authService = new AuthService();
export default authService;
